import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowRight,
  Cpu,
  Server,
  Network,
  HardDrive,
  Monitor,
  Microchip,
  ShieldCheck,
  Globe2,
  Truck,
  Sparkles,
  Clock,
  Headphones,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Overline from "@/components/Overline";
import BrandMarquee from "@/components/BrandMarquee";
import {
  COMPANY,
  PRODUCT_CATEGORIES,
  STATS,
  VALUE_PROPS,
} from "@/data/site";
import { useQuoteModal } from "@/context/QuoteModalContext";

const CATEGORY_ICONS = {
  "computers-laptops": Monitor,
  components: Cpu,
  "servers-storage": Server,
  networking: Network,
  semiconductors: Microchip,
  accessories: HardDrive,
};

const VALUE_ICONS = [Globe2, Truck, ShieldCheck, Clock, Sparkles, Headphones];

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <BrandMarquee />
      <Categories />
      <WhyChoose />
      <AboutTeaser />
      <CTA />
    </div>
  );
}

function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden border-b border-slate-200"
    >
      <div className="tech-grid tech-grid-fade absolute inset-0 opacity-60" aria-hidden="true" />
      <div className="absolute -right-32 top-10 h-[520px] w-[520px] rounded-full bg-cyan-100/70 blur-3xl" aria-hidden="true" />

      <div className="relative mx-auto grid max-w-7xl gap-14 px-5 py-20 sm:px-8 lg:grid-cols-12 lg:gap-10 lg:py-32">
        <div className="lg:col-span-7">
          <Overline>Dubai · IFZA · Global Trading</Overline>
          <h1 className="font-heading mt-6 text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl lg:text-[64px] lg:leading-[1.04]">
            Global Technology.
            <br />
            <span className="text-cyan-600">Reliable Solutions.</span>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-700 sm:text-lg">
            {COMPANY.legalName} is a Dubai-based technology trading and
            solutions company supplying computers, IT hardware, electronic
            components, networking equipment and technology products to
            businesses across international markets.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-3">
            <Link to="/products" data-testid="hero-cta-products">
              <Button
                size="lg"
                data-testid="hero-cta-products-btn"
                className="rounded-md bg-cyan-600 px-6 py-6 text-base font-medium text-white hover:bg-cyan-700"
              >
                Explore Our Products
                <ArrowUpRight className="ml-1.5 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact" data-testid="hero-cta-contact">
              <Button
                size="lg"
                variant="outline"
                data-testid="hero-cta-contact-btn"
                className="rounded-md border-slate-300 bg-transparent px-6 py-6 text-base font-medium text-slate-900 hover:border-cyan-600 hover:bg-transparent hover:text-cyan-700"
              >
                Contact Our Team
                <ArrowRight className="ml-1.5 h-5 w-5" />
              </Button>
            </Link>
          </div>

          <dl
            data-testid="hero-stats"
            className="mt-14 grid max-w-2xl grid-cols-2 gap-x-8 gap-y-6 border-t border-slate-200 pt-8 sm:grid-cols-4"
          >
            {STATS.map((s) => (
              <div key={s.label}>
                <dt className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500">
                  {s.label}
                </dt>
                <dd className="font-heading mt-1 text-3xl font-semibold text-slate-900">
                  {s.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="relative lg:col-span-5">
          <div className="relative aspect-[4/5] overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
            <img
              src="https://images.pexels.com/photos/37730211/pexels-photo-37730211.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt="Server rack with blue lighting"
              className="h-full w-full object-cover opacity-90"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-white via-white/50 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-cyan-700">
                / 01 — INFRASTRUCTURE
              </div>
              <div className="mt-2 font-heading text-xl font-medium text-slate-900">
                Data-centre grade hardware, sourced globally.
              </div>
            </div>
          </div>

          <div className="absolute -left-6 -top-6 hidden h-24 w-24 rounded-lg border border-cyan-500/50 lg:block" aria-hidden="true" />
        </div>
      </div>
    </section>
  );
}

function Categories() {
  return (
    <section
      data-testid="categories-section"
      className="relative border-b border-slate-200"
    >
      <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <Overline>Products & Services</Overline>
            <h2 className="font-heading mt-5 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
              Everything your IT stack needs — from a single Dubai partner.
            </h2>
          </div>
          <p className="max-w-md text-sm text-slate-500">
            From individual semiconductors to full data-centre rollouts. Browse
            our core categories or request a custom quotation.
          </p>
        </div>

        <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {PRODUCT_CATEGORIES.map((cat, idx) => {
            const Icon = CATEGORY_ICONS[cat.slug] ?? Cpu;
            return (
              <Link
                key={cat.slug}
                to="/products"
                data-testid={`category-card-${cat.slug}`}
                className="group relative flex flex-col gap-4 overflow-hidden rounded-xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-cyan-600/70 hover:bg-slate-100"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500">
                    / {String(idx + 1).padStart(2, "0")}
                  </span>
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white text-cyan-700 transition-colors group-hover:border-cyan-600/70">
                    <Icon className="h-4 w-4" />
                  </span>
                </div>
                <div>
                  <h3 className="font-heading text-xl font-medium text-slate-900">
                    {cat.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">
                    {cat.summary}
                  </p>
                </div>
                <div className="mt-auto flex items-center gap-1.5 text-sm font-medium text-cyan-700 opacity-0 transition-opacity group-hover:opacity-100">
                  View details
                  <ArrowUpRight className="h-4 w-4" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function WhyChoose() {
  return (
    <section
      data-testid="why-choose-section"
      className="relative border-b border-slate-200 bg-white"
    >
      <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Overline>Why GTECGLOBAL</Overline>
            <h2 className="font-heading mt-5 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
              Built for global B2B technology trading.
            </h2>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-slate-500">
              Six reasons businesses choose us as their long-term sourcing
              partner — from competitive pricing to end-to-end logistics.
            </p>
          </div>

          <div className="lg:col-span-8">
            <div className="grid grid-cols-1 divide-y divide-slate-200 border-y border-slate-200 sm:grid-cols-2 sm:divide-x sm:[&>*:nth-child(2n)]:border-r-0">
              {VALUE_PROPS.map((v, idx) => {
                const Icon = VALUE_ICONS[idx % VALUE_ICONS.length];
                return (
                  <div
                    key={v.code}
                    data-testid={`value-prop-${v.code}`}
                    className="group relative flex flex-col gap-3 border-slate-200 p-6 transition-colors hover:bg-slate-50 sm:p-8"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-cyan-600">
                        / {v.code}
                      </span>
                      <Icon className="h-4 w-4 text-slate-500 transition-colors group-hover:text-cyan-700" />
                    </div>
                    <h3 className="font-heading text-lg font-medium text-slate-900">
                      {v.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-slate-500">
                      {v.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function AboutTeaser() {
  return (
    <section
      data-testid="about-teaser-section"
      className="relative border-b border-slate-200"
    >
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-24 sm:px-8 lg:grid-cols-12 lg:py-32">
        <div className="relative lg:col-span-5">
          <div className="relative aspect-[4/5] overflow-hidden rounded-2xl border border-slate-200">
            <img
              src="https://images.pexels.com/photos/5087165/pexels-photo-5087165.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt="Dubai skyline at night"
              className="h-full w-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-white/90 via-white/30 to-transparent" />
          </div>
        </div>

        <div className="lg:col-span-7 lg:pt-6">
          <Overline>About GTECGLOBAL</Overline>
          <h2 className="font-heading mt-5 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            Your Trusted Technology Partner.
          </h2>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-700">
            At {COMPANY.shortName}, we help businesses source reliable
            technology products and IT equipment from trusted manufacturers
            and suppliers. Our focus is on product quality, competitive
            pricing, dependable service, and long-term business relationships.
          </p>

          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            {[
              { k: "Headquarters", v: "Dubai, UAE · IFZA Business Park" },
              { k: "Specialisation", v: "IT hardware, components, networking" },
              { k: "Reach", v: "Servicing clients across 40+ countries" },
              { k: "Model", v: "B2B trading, projects & bulk procurement" },
            ].map((f) => (
              <div
                key={f.k}
                className="rounded-lg border border-slate-200 bg-slate-50 p-5"
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-cyan-600">
                  {f.k}
                </div>
                <div className="mt-2 text-sm text-slate-700">{f.v}</div>
              </div>
            ))}
          </div>

          <div className="mt-10">
            <Link to="/about" data-testid="about-teaser-cta">
              <Button
                variant="outline"
                data-testid="about-teaser-cta-btn"
                className="rounded-md border-slate-300 bg-transparent text-slate-900 hover:border-cyan-600 hover:bg-transparent hover:text-cyan-700"
              >
                Learn more about us
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function CTA() {
  const { openModal } = useQuoteModal();
  return (
    <section
      data-testid="cta-section"
      className="relative overflow-hidden border-b border-slate-200"
    >
      <div className="tech-grid tech-grid-fade absolute inset-0 opacity-40" aria-hidden="true" />
      <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
        <div className="relative overflow-hidden rounded-2xl border border-cyan-500/40 bg-gradient-to-br from-slate-900 via-white to-slate-900 p-10 sm:p-16">
          <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-cyan-100 blur-3xl" aria-hidden="true" />
          <div className="relative grid gap-8 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-8">
              <Overline>Ready when you are</Overline>
              <h2 className="font-heading mt-4 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
                Request a quotation in 24 hours.
              </h2>
              <p className="mt-4 max-w-2xl text-base text-slate-700">
                Tell us what you need — quantities, brands, destination — and
                our team will revert with a detailed quote and lead time.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3 lg:col-span-4 lg:justify-end">
              <Button
                data-testid="cta-section-quote-btn"
                onClick={() => openModal()}
                className="rounded-md bg-cyan-600 px-6 py-5 text-base font-medium text-white hover:bg-cyan-700"
              >
                Get a Quote
                <ArrowUpRight className="ml-1.5 h-5 w-5" />
              </Button>
              <Link to="/contact" data-testid="cta-section-contact">
                <Button
                  variant="outline"
                  data-testid="cta-section-contact-btn"
                  className="rounded-md border-slate-300 bg-transparent px-6 py-5 text-base font-medium text-slate-900 hover:border-cyan-600 hover:bg-transparent hover:text-cyan-700"
                >
                  Contact Us
                  <ArrowRight className="ml-1.5 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
